import React, { Component, ChangeEvent, RefObject, createRef } from "react";
import {
  Button,
  Checkbox,
  TextField,
  Chip,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  Skeleton,
} from "@mui/material";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList, VariableSizeList, ListChildComponentProps } from "react-window";
import { IFilterParams } from "ag-grid-community";
import { IFilterReactComp } from "ag-grid-react";
import { AxiosInstance } from "axios";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import styles from "./CheckboxFilter.module.scss";
import { LoadingStatus } from "types/app";

interface CheckboxFilterParams {
  keyword: string;
  data: CheckboxItem[];
  filterActive: boolean;
  loadingStatus: LoadingStatus;
  storedData: string;
  filterType: string;
}

export interface CheckboxItem {
  checked: boolean;
  name: string;
  value: string;
}

class ResellerFilter
  extends Component<IFilterParams, CheckboxFilterParams>
  implements IFilterReactComp
{
  private listRef: RefObject<VariableSizeList>;
  private title: string;

  constructor(props: IFilterParams) {
    super(props);
    this.state = {
      keyword: "",
      data: [],
      filterActive: false,
      loadingStatus: "notLoaded",
      storedData: "",
      filterType: "in",
    };

    this.listRef = createRef<VariableSizeList>();

    this.onButtonClick = this.onButtonClick.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.onkeywordChange = this.onkeywordChange.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.handleClearKeyword = this.handleClearKeyword.bind(this);
    this.handleLabelClick = this.handleLabelClick.bind(this);
    this.handleFilterType = this.handleFilterType.bind(this);

    this.title = this.props.colDef.filterParams?.title ?? props.colDef.headerName;
  }

  componentDidMount() {
    const { queryParams, axios, nullOptionName, customPath } = this.props.colDef.filterParams;

    (axios as AxiosInstance)
      .get<string[]>(customPath ?? `/api/portfolio/metadata/${queryParams}`, {})
      .then((res) => {
        const data = res.data;
        const displayData: CheckboxItem[] = data.map((i) => ({
          checked: false,
          name: i,
          value: i,
        }));

        if (nullOptionName) {
          displayData.unshift({
            checked: false,
            name: nullOptionName,
            value: "null",
          });
        }

        this.setState({
          data: displayData,
          loadingStatus: "loaded",
        });
      })
      .catch((error) => {
        console.error(error);
        this.setState({ loadingStatus: "failed" });
      });
  }
  // Return true if the filter is active. If active then 1) the grid will show the filter icon in the column
  // header and 2) the filter will be included in the filtering of the data.
  isFilterActive() {
    return this.state.filterActive;
  }

  doesFilterPass() {
    return true;
  }

  afterGuiAttached() {
    const { storedData, loadingStatus, data } = this.state;
    if (storedData && loadingStatus === "loaded") {
      const valueArray = storedData.split("|");
      this.setState({
        data: data.map((x) =>
          valueArray.some((i) => i === x.value) ? { ...x, checked: true } : x
        ),
        storedData: "",
      });
    }
  }

  onItemChange(action: "add" | "remove" | "edit", item: CheckboxItem) {
    const { data } = this.state;
    if (action === "add") {
      this.setState({ data: [item, ...data] });
    } else if (action === "remove") {
      const removeItem = data.find((x) => x.value === item.value);
      this.setState({ data: data.filter((x) => x.value !== item.value) }, () => {
        if (removeItem?.checked) {
          this.props.filterChangedCallback();
        }
      });
    } else if (action === "edit") {
      const editItem = data.find((x) => x.value === item.value);
      this.setState(
        {
          data: data.map((x) =>
            x.value === item.value ? { ...x, name: item.name, value: item.name } : x
          ),
        },
        () => {
          this.listRef.current?.resetAfterIndex(0, true);
          if (editItem?.checked) {
            this.props.filterChangedCallback();
          }
        }
      );
    }
  }

  getModel() {
    if (this.state.storedData) {
      return {
        filterType: "resellerFilter",
        type: this.state.filterType,
        filter: this.state.storedData,
      };
    } else if (this.state.filterActive && this.state.loadingStatus === "loaded") {
      const checkboxFilterValue = this.state.data
        .filter((x) => x.checked)
        .map((x) => x.value)
        .join("|");
      return checkboxFilterValue.length
        ? {
            filterType: "resellerFilter",
            type: this.state.filterType,
            filter: checkboxFilterValue,
          }
        : null;
    } else {
      return null;
    }
  }

  setModel(model: any) {
    if (!!model) {
      const { filter } = model;
      if (filter) {
        if (this.state.loadingStatus === "loaded") {
          const valueArray = (filter as string).split("|");
          const tempData = this.state.data.map((i) =>
            valueArray.includes(i.value) ? { ...i, checked: true } : { ...i, checked: false }
          );
          this.setState(
            {
              data: tempData,
              filterActive: true,
            },
            () => {
              this.props.filterChangedCallback();
              this.listRef.current?.resetAfterIndex(0, true);
            }
          );
        } else {
          this.setState({ storedData: filter, filterActive: true }, () => {
            this.props.filterChangedCallback();
            this.listRef.current?.resetAfterIndex(0, true);
          });
        }
      }
    } else {
      this.setState(
        {
          filterActive: false,
          storedData: "",
          keyword: "",
          data: this.state.data.map((i) => ({ ...i, checked: false })),
        },
        () => {
          this.props.filterChangedCallback();
          this.listRef.current?.resetAfterIndex(0, true);
        }
      );
    }
  }

  onButtonClick(type: "clear" | "apply") {
    switch (type) {
      case "clear":
        return () => {
          const tempData = this.state.data.map((item) => ({
            ...item,
            checked: false,
          }));
          this.setState(
            {
              data: tempData,
              keyword: "",
              filterActive: false,
            },
            () => {
              this.listRef.current?.resetAfterIndex(0, true);
              this.props.filterChangedCallback();
            }
          );
        };
      case "apply":
        return () => {
          this.setState({ filterActive: true }, () => {
            this.props.filterChangedCallback();
          });
        };
    }
  }

  handleChange(e: ChangeEvent<HTMLInputElement>) {
    const tempData = this.state.data.map((i) =>
      i.name === e.target.name ? { ...i, checked: e.target.checked } : i
    );
    this.setState({ data: tempData }, () => {
      this.listRef.current?.resetAfterIndex(0, true);
    });
  }

  onkeywordChange(e: ChangeEvent<HTMLInputElement>) {
    this.setState({ keyword: e.target.value }, () => {
      this.listRef.current?.resetAfterIndex(0, true);
    });
  }

  handleDelete = (item: CheckboxItem) => () => {
    const tempData = this.state.data.map((i) =>
      i.name === item.name ? { ...i, checked: false } : i
    );
    this.setState({ data: tempData }, () => {
      this.listRef.current?.resetAfterIndex(0, true);
    });
  };

  handleLabelClick = (item: CheckboxItem) => () => {
    const tempData = this.state.data.map((i) =>
      i.value === item.value ? { ...i, checked: !item.checked } : i
    );
    this.setState({ data: tempData }, () => {
      this.listRef.current?.resetAfterIndex(0, true);
    });
  };

  handleClearKeyword = () => {
    this.setState({ keyword: "" }, () => {
      this.listRef.current?.resetAfterIndex(0, true);
    });
  };

  handleFilterType = (event: ChangeEvent<HTMLInputElement>) => {
    this.setState({ filterType: event.target.value });
  };

  RowRenderer = ({ index, style }: ListChildComponentProps) => {
    const { data, keyword } = this.state;
    const list = data.filter((x) => x.name?.toLowerCase().includes(keyword.toLowerCase()));
    const item = list[index];
    return (
      <ListItem style={{ ...style, padding: 0 }}>
        <ListItemIcon>
          <Checkbox
            checked={item.checked}
            name={item.name}
            onChange={this.handleChange}
            color="primary"
            classes={{
              colorPrimary: styles.checkboxColorPrimary,
            }}
          />
        </ListItemIcon>
        <ListItemText
          primary={item.name}
          onClick={this.handleLabelClick(item)}
          style={{ cursor: "pointer" }}
        />
      </ListItem>
    );
  };

  render() {
    const { data, keyword, loadingStatus, filterActive } = this.state;
    const checked = data.filter((x) => x.checked);

    if (loadingStatus === "failed") {
      return (
        <div className={styles.failLoad}>
          <span>Sorry, fail to load data</span>
        </div>
      );
    }

    const filteredData = data.filter((x) => x.name?.toLowerCase().includes(keyword.toLowerCase()));

    return (
      <div>
        <div className={styles.filterWrapper}>
          <div className={styles.chipWrapper}>
            {checked.slice(0, 10).map((item) => (
              <Chip
                label={item.name}
                onDelete={this.handleDelete(item)}
                key={item.name}
                size="small"
                color="primary"
                classes={{
                  root: styles.chipRoot,
                  label: styles.chipLabel,
                }}
              />
            ))}
            {checked.length > 10 && <div>{`${checked.length - 10} more`}</div>}
          </div>

          <TextField
            fullWidth
            placeholder="Enter Search Criteria..."
            variant="standard"
            margin="dense"
            autoFocus
            value={keyword}
            onChange={this.onkeywordChange}
            InputProps={{
              endAdornment: keyword ? (
                <IconButton
                  onClick={this.handleClearKeyword}
                  size="small"
                >
                  <HighlightOffOutlinedIcon
                    color="primary"
                    fontSize="small"
                  />
                </IconButton>
              ) : null,
            }}
          />

          <div style={{ textAlign: "center" }}>
            {loadingStatus === "loaded" ? (
              <div style={{ height: 200 }}>
                <AutoSizer>
                  {({ width, height }) => (
                    <VariableSizeList
                      ref={this.listRef}
                      width={width}
                      height={height}
                      itemCount={filteredData.length}
                      itemSize={() => 42} // fixed height for each row
                    >
                      {this.RowRenderer}
                    </VariableSizeList>
                  )}
                </AutoSizer>
              </div>
            ) : (
              <>
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton
                    key={i}
                    width={160}
                    height={28}
                    variant="rectangular"
                    style={{ margin: "6px 12px" }}
                  />
                ))}
              </>
            )}
          </div>
        </div>

        <Divider />

        <RadioGroup
          defaultValue={this.state.filterType}
          style={{ display: "flex", flexDirection: "row" }}
          onChange={this.handleFilterType}
        >
          <FormControlLabel
            value="in"
            label="Include"
            control={<Radio />}
          />
          <FormControlLabel
            value="notIn"
            label="Exclude"
            control={<Radio />}
          />
        </RadioGroup>

        <div className="ag-filter-apply-panel">
          <Button
            variant="text"
            disabled={!filterActive}
            onClick={this.onButtonClick("clear")}
            className="ag-standard-button ag-filter-apply-panel-button"
          >
            Reset filter
          </Button>
          <Button
            disabled={!data.find((i) => i.checked)}
            variant="text"
            onClick={this.onButtonClick("apply")}
            className="ag-standard-button ag-filter-apply-panel-button"
          >
            Apply filter
          </Button>
        </div>
      </div>
    );
  }
}

export default ResellerFilter;
